import React from 'react';
import PropTypes from 'prop-types';

// Common components
import Spinner from '../Spinner/Spinner';
import Tooltip from '../Tooltip/Tooltip';

// Tooltip constants
import * as TooltipText from './CropToolbarTooltips';

// App Config
import AppConfiguration from '../../../services/AppConfiguration';

// Component Styles
import './cropToolbar.scss';

/**
 * This component shows a toolbar with the available options for editing a image.
 * @extends {Component}
 */
class CropToolbar extends React.Component {
  constructor(props) {
    super(props);

    /**
     * Instance of the App configuration data.
     * @type {Object}
     */
    this.appConfig = AppConfiguration.getConfig();
  }

  /**
   * Returns the button for image rotation with onClick, onKeyPress handlers.
   * @return {ReactElement}
   */
  renderRotateIcon() {
    const isDisabled = !this.props.isRotateButtonEnabled;
    const rotateIconSrc = isDisabled ?
      `${this.appConfig.cdnUrl.url}/rotate-disabled.svg` :
      `${this.appConfig.cdnUrl.url}/rotate.svg`;
    const disabledClassModifier = isDisabled ? '-disabled' : '';

    return (
      <Tooltip
        text={TooltipText.ROTATE_BUTTON}
        trigger='none'
        visible={this.props.showRotateTooltip}
        className='wtfCropToolbar_rotateIcon'
        width='350px'
      >
        <button
          disabled={isDisabled}
          onClick={this.props.onRotateButtonClick}
          onKeyPress={this.props.onRotateButtonClick}
          className={`wtfCropToolbar_rotateButton ${disabledClassModifier}`}
        >
          <img src={rotateIconSrc} alt='Rotate Icon' className='wtfCropToolbar_rotateIcon' />
        </button>
      </Tooltip>
    );
  }

  /**
   * Renders the left rotation buttons. It includes:
   * - Cancel button
   * - Rotate 90deg left
   * - Rotate 10deg left
   * @return {ReactElement}
   */
  renderRotateLeft() {
    return (
      <div className='wtfCropToolbar_leftRotation'>
        <button
          onClick={this.props.onCancelRotation}
          onKeyPress={this.props.onCancelRotation}
          className='wtfCropToolbar_cancelRotationButton'
        >
          Cancel
        </button>
        <div className='wtfCropToolbar_rotationButtonsWrapper'>
          <Tooltip
            text={TooltipText.ROTATE_90_LEFT}
            className='wtfCropToolbar_rotateLeftButton'
          >
            <button
              onClick={() => this.props.onIncrementalRotationButtonClick(-90)}
            >
              <img src={`${this.appConfig.cdnUrl.url}/rotate-90-left.png`} alt='Rotate 90º left' />
            </button>
          </Tooltip>
          <Tooltip
            text={TooltipText.ROTATE_10_LEFT}
            className='wtfCropToolbar_rotateLeftButton'
          >
            <button
              onClick={() => this.props.onIncrementalRotationButtonClick(-10)}
            >
              <img src={`${this.appConfig.cdnUrl.url}/rotate-10-left.png`} alt='Rotate 10º left' />
            </button>
          </Tooltip>
        </div>
      </div>
    );
  }

  /**
   * Renders the right rotation buttons. It includes:
   * - Rotate 90deg right
   * - Rotate 10deg right
   * @return {ReactElement}
   */
  renderRotateRight() {
    return (
      <div className='wtfCropToolbar_rightRotation'>
        <div className='wtfCropToolbar_rotationButtonsWrapper'>
          <Tooltip
            text={TooltipText.ROTATE_10_RIGHT}
            className='wtfCropToolbar_rotateRightButton'
          >
            <button
              onClick={() => this.props.onIncrementalRotationButtonClick(10)}
            >
              <img src={`${this.appConfig.cdnUrl.url}/rotate-10-right.png`} alt='Rotate 10º right' />
            </button>
          </Tooltip>
          <Tooltip
            text={TooltipText.ROTATE_90_RIGHT}
            className='wtfCropToolbar_rotateRightButton'
          >
            <button
              onClick={() => this.props.onIncrementalRotationButtonClick(90)}
            >
              <img src={`${this.appConfig.cdnUrl.url}/rotate-90-right.png`} alt='Rotate 90º right' />
            </button>
          </Tooltip>
        </div>
      </div>
    );
  }

  /**
   * @return Render the search active icon or the disabled icon according a component prop. If the loading props
   * is defined, it render the spinner component.
   */
  renderSearchIcon() {
    const isDisabled = !this.props.isSearchButtonActive && !this.props.isRotationModeActive;
    const cropIconSrc = isDisabled ? `${this.appConfig.cdnUrl.url}/search-disabled.svg`
      : `${this.appConfig.cdnUrl.url}/search.svg`;
    const disabledClassModifier = isDisabled ? '-disabled' : '';
    let dataToRender;

    // The close callback is only needed when the icon is active, since the store needs to be updated
    const tooltipCloseCallback = !isDisabled ? this.props.onNextButtonTooltipClosed : () => {};
    const tooltipText = isDisabled ? TooltipText.NEXT_BUTTON_DISABLED : TooltipText.NEXT_BUTTON_ENABLED;
    const tooltipVisible = !isDisabled && this.props.isSearchButtonActive && !this.props.showRotateTooltip;
    const tooltipEnabled = isDisabled ?
      this.props.isManualCropButtonActive && !this.props.isManualCropButtonHighlighted :
      this.props.showNextButtonEnabledTooltip && !this.props.showRotateTooltip && !this.props.isRotationModeActive;

    if (this.props.loading) {
      dataToRender = (
        <div className='wtfCropToolbar_searchLoading'>
          <Spinner />
        </div>
      );
    } else {
      dataToRender = (
        <Tooltip
          text={tooltipText}
          trigger='click'
          enabled={tooltipEnabled}
          visible={tooltipVisible}
          width='300px'
          onTooltipClose={tooltipCloseCallback}
          activateOverlay={isDisabled}
        >
          <button
            disabled={isDisabled}
            onClick={this.props.onSearchButtonClick}
            onKeyPress={this.props.onSearchButtonClick}
            className={`wtfCropToolbar_searchButton ${disabledClassModifier}`}
          >
            <img src={cropIconSrc} alt='Search Icon' className='wtfCropToolbar_icon' />
          </button>
        </Tooltip>
      );
    }

    return dataToRender;
  }

  /**
   * Returns the button for manual cropping with onClick, onKeyPress handlers, and logic
   * that checks if the button should be enabled or not.
   * @return {ReactElement}
   */
  renderCropButton() {
    const isDisabled = !this.props.isManualCropButtonActive;
    const isHighlighted = this.props.isManualCropButtonHighlighted && !isDisabled;
    let cropIconSrc = `${this.appConfig.cdnUrl.url}/crop.svg`;
    const disabledClassModifier = isDisabled ? '-disabled' : '';

    if (isHighlighted && !isDisabled) {
      cropIconSrc = `${this.appConfig.cdnUrl.url}/crop-highlighted.svg`;
    } else if (isDisabled) {
      cropIconSrc = `${this.appConfig.cdnUrl.url}/crop-disabled.svg`;
    }

    return (
      <Tooltip
        text={TooltipText.CROP_BUTTON}
        trigger='none'
        visible={isHighlighted}
        className='wtfCropToolbar_cropButtonWrapper'
        width='360px'
      >
        <button
          disabled={isDisabled}
          onClick={this.props.onCropButtonClick}
          onKeyPress={this.props.onCropButtonClick}
          className={`wtfCropToolbar_cropButton ${disabledClassModifier}`}
        >
          <img src={cropIconSrc} alt='Crop Icon' className='wtfCropToolbar_icon' />
        </button>
      </Tooltip>
    );
  }

  /**
   * Renders the left section of the toolbar. If the rotation mode is enabled, it will
   * render the rotation buttons. Otherwise, the button that enables the rotation mode.
   * @return {ReactElement}
   */
  renderLeftButtons() {
    let renderData;

    if (this.props.isRotationModeActive) {
      renderData = this.renderRotateLeft();
    } else {
      renderData = this.renderRotateIcon();
    }

    return renderData;
  }

  /**
   * Renders the right section of the toolbar. If the rotation mode is enabled, it will
   * render the rotation buttons. The crop button otherwise.
   * @return {ReactElement}
   */
  renderRightButtons() {
    let renderData;

    if (this.props.isRotationModeActive) {
      renderData = this.renderRotateRight();
    } else {
      renderData = this.renderCropButton();
    }

    return renderData;
  }

  render() {
    return (
      <div className='wtfCropToolbar'>
        {this.renderLeftButtons()}
        {this.renderSearchIcon()}
        {this.renderRightButtons()}
      </div>
    );
  }
}

/**
 * Set the CropToolbar component props default values.
 * @type {Object}
 */
CropToolbar.defaultProps = {
  loading: false,
  isManualCropButtonHighlighted: false,
  isRotationModeActive: false,
  onSearchButtonClick: () => {},
  onNextButtonTooltipClosed: () => {},
  onCropButtonClick: () => {},
  onCancelRotation: () => {},
  onIncrementalRotationButtonClick: () => {},
  onRotateButtonClick: () => {},
};

/**
 * Declare the CropToolbar component PropTypes for better validation.
 * @type {Object}
 */
CropToolbar.propTypes = {
  loading: PropTypes.bool,
  isRotationModeActive: PropTypes.bool,
  isRotateButtonEnabled: PropTypes.bool.isRequired,
  isManualCropButtonActive: PropTypes.bool.isRequired,
  isManualCropButtonHighlighted: PropTypes.bool,
  isSearchButtonActive: PropTypes.bool.isRequired,
  showNextButtonEnabledTooltip: PropTypes.bool.isRequired,
  showRotateTooltip: PropTypes.bool.isRequired,
  onCancelRotation: PropTypes.func,
  onCropButtonClick: PropTypes.func,
  onIncrementalRotationButtonClick: PropTypes.func,
  onRotateButtonClick: PropTypes.func,
  onSearchButtonClick: PropTypes.func,
  onNextButtonTooltipClosed: PropTypes.func,
};

export default CropToolbar;



// WEBPACK FOOTER //
// ./src/components/common/CropToolbar/CropToolbar.js